import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, output, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Params } from '@angular/router';
import { DateTimeDisplayComponent } from '@components/date-time-display/date-time-display.component';
import { PaginationComponent } from '@components/pagination/pagination.component';
import { IconoirCalendar, IconoirXmark } from '@ey-xd/motif-icon';
import {
  MotifCardModule,
  MotifChipModule,
  MotifFormsModule,
  MotifIconModule,
  MotifModule,
  MotifPaginationModule,
} from '@ey-xd/ng-motif';
import { IDataConnector, PageSizeOptions, WhatsNewNotificationItemDto } from '@models';
import { DATE_TIME_FORMAT } from '@models/common.models';
import { Option } from '@models/pagination-select-option';
import { TranslatePipe } from '@ngx-translate/core';
import { NotificationService } from '@services/webapi/notification.service';
import { PageComponent } from '../../layout/main/page/page.component';
@Component({
  selector: 'app-news',
  standalone: true,
  imports: [
    CommonModule,
    MotifFormsModule,
    ReactiveFormsModule,
    FormsModule,
    MotifModule,
    MotifCardModule,
    MotifIconModule,
    MotifChipModule,
    MotifPaginationModule,
    PaginationComponent,
    PageComponent,
    TranslatePipe,
    DateTimeDisplayComponent,
  ],
  providers: [DatePipe],
  templateUrl: './news.component.html',
  styleUrl: './news.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewsComponent implements OnInit {
  private notificationService = inject(NotificationService);
  private sanitizer = inject(DomSanitizer);

  public FORMAT: typeof DATE_TIME_FORMAT = DATE_TIME_FORMAT;
  notifications: WhatsNewNotificationItemDto[] = [];
  filteredNotifications: WhatsNewNotificationItemDto[] = [];
  searchItem = '';
  hideClearButton = false;
  form: FormGroup;
  totalItems = 0;
  totalPages = 0;
  dataConnector = input.required<IDataConnector>();
  pageSizeOptions: Option[] = PageSizeOptions;
  currentPageSize: Option = this.pageSizeOptions[0];
  currentPage = signal<number>(1);
  numberOfPages = signal<number>(0);
  numberOfRows = signal<number>(0);
  pageChangedEvent = output<number>();
  pageSizeChangedEvent = output<Option>();
  constructor() {
    this.form = new FormGroup({
      search: new FormControl({ value: this.searchItem, disabled: false }, [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.loadNotifications();
    this.hideClearButton = false;
  }

  loadNotifications(): void {
    const params: Params = {
      pageSize: this.currentPageSize.value,
      pageNumber: this.currentPage(),
      sortExpression: 'publishedOn Desc',
      search: this.searchItem || null,
    };
    this.notificationService.getNotifications(params).subscribe({
      next: response => {
        this.notifications = response.items.map(item => ({
          ...item,
          notificationText: item.notificationText,
          sanitizedNotificationText: this.sanitizer.bypassSecurityTrustHtml(item.notificationText), // Create a separate sanitized property
        }));
        this.filteredNotifications = this.notifications;
        this.totalItems = response.totalCount;
        this.numberOfRows.set(this.totalItems);
        this.calculateTotalPages();
      },
      error: error => {
        console.error('Error loading notifications:', error);
      },
    });
  }
  calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalItems / this.currentPageSize.value);
    this.numberOfPages.set(this.totalPages);
  }
  clearValue = () => {
    this.searchItem = '';
    this.form.get('search')?.setValue('');
    this.hideClearButton = false;
    this.filteredNotifications = this.notifications;
    this.totalItems = this.filteredNotifications.length;
    this.totalPages = Math.ceil(this.totalItems / this.currentPageSize.value);
    this.loadNotifications();
  };

  private getSourceList(): WhatsNewNotificationItemDto[] {
    if (!this.searchItem) return this.notifications;

    const term = this.searchItem.trim().toLowerCase();

    return this.notifications.filter(notification => {
      const titleMatch = notification.title?.toLowerCase().includes(term);
      const textMatch = notification.notificationText?.toLowerCase().includes(term);
      return titleMatch || textMatch;
    });
  }
  formatDate(date: Date): string {
    const timeOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    };
    const timePart = new Intl.DateTimeFormat('en-US', timeOptions).format(date);
    const day = new Intl.DateTimeFormat('en-US', { day: '2-digit' }).format(date);
    const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
    const year = new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(date);
    const formattedDatePart = `${day} ${month} ${year}`;
    return `${timePart} | ${formattedDatePart}`;
  }

  public pageChanged(newPage: number): void {
    this.currentPage.set(newPage);
    this.loadNotifications();
    //this.dataConnector().onPageChange(newPage, this.currentPageSize.value);
  }

  public pageSizeChanged(newPageSize: Option): void {
    this.currentPage.set(1);
    this.currentPageSize = newPageSize;
    this.loadNotifications();
    //this.dataConnector().onPageChange(1, newPageSize.value);
  }

  onSearchInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.searchItem = inputElement?.value.trim().toLowerCase();
    this.hideClearButton = this.searchItem.length === 0;
    this.filterNotifications();
    this.loadNotifications();
  }
  filterNotifications(): void {
    const baseList = this.getSourceList();
    this.calculateTotalPages();
    this.filteredNotifications = baseList.slice(0, this.currentPageSize.value);
  }
  isNewNotification(publishedOn: Date): boolean {
    const publishDateObj = new Date(publishedOn);
    const currentDate = new Date();
    const timeDifference = currentDate.getTime() - publishDateObj.getTime();
    const daysDifference = timeDifference / (1000 * 3600 * 24);
    return daysDifference <= 7;
  }
  myIconFunction() {
    return IconoirCalendar;
  }
  myIconClearFunction() {
    return IconoirXmark;
  }
}
